
import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { PolicyType } from '../types/PolicyTypes';

interface LiquidFundsPolicySectionProps {
  policy: PolicyType;
  setPolicy: React.Dispatch<React.SetStateAction<PolicyType>>;
  isEditing: boolean;
}

const LiquidFundsPolicySection = ({ policy, setPolicy, isEditing }: LiquidFundsPolicySectionProps) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Liquid Mutual Funds Policy</h3>
        <div className="flex items-center space-x-2">
          <Label htmlFor="lfEnabled">Enable Liquid Funds</Label>
          <Switch 
            id="lfEnabled"
            checked={policy.liquidFunds.enabled}
            onCheckedChange={(checked) => setPolicy({
              ...policy,
              liquidFunds: {
                ...policy.liquidFunds,
                enabled: checked
              }
            })}
            disabled={!isEditing}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="lfMaxPercentage">Maximum Allocation in Liquid Funds (%)</Label>
          <Input 
            id="lfMaxPercentage" 
            type="number" 
            value={policy.liquidFunds.maxPercentage} 
            onChange={(e) => setPolicy({
              ...policy,
              liquidFunds: {
                ...policy.liquidFunds,
                maxPercentage: parseFloat(e.target.value)
              }
            })}
            disabled={!isEditing || !policy.liquidFunds.enabled}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lfMinRating">Minimum Fund Rating</Label>
          <Select 
            defaultValue={policy.liquidFunds.minRating}
            disabled={!isEditing || !policy.liquidFunds.enabled}
            onValueChange={(value) => setPolicy({
              ...policy,
              liquidFunds: {
                ...policy.liquidFunds,
                minRating: value
              }
            })}
          >
            <SelectTrigger id="lfMinRating">
              <SelectValue placeholder="Select minimum rating" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AAA">AAA</SelectItem>
              <SelectItem value="AA+">AA+</SelectItem>
              <SelectItem value="AA">AA</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="lfExpectedReturn">Expected Annual Returns (%)</Label>
          <Input 
            id="lfExpectedReturn" 
            type="number" 
            step="0.01"
            value={policy.liquidFunds.expectedReturns} 
            onChange={(e) => setPolicy({
              ...policy,
              liquidFunds: {
                ...policy.liquidFunds,
                expectedReturns: parseFloat(e.target.value)
              }
            })}
            disabled={!isEditing || !policy.liquidFunds.enabled}
          />
        </div>
        <div className="flex items-center space-x-2">
          <Switch 
            id="lfAutoRedemption"
            checked={policy.liquidFunds.autoRedemption}
            onCheckedChange={(checked) => setPolicy({
              ...policy,
              liquidFunds: {
                ...policy.liquidFunds,
                autoRedemption: checked
              }
            })}
            disabled={!isEditing || !policy.liquidFunds.enabled}
          />
          <Label htmlFor="lfAutoRedemption">Allow Automatic Redemption for Liquidity Needs</Label>
        </div>
      </div>
    </div>
  );
};

export default LiquidFundsPolicySection;
